import React, {useContext, useEffect, useState} from 'react';
import Link from 'next/link';
import styles from './BookingForm.module.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import BusinessService from '../../services/business.service';
import UserService from '../../services/user.service';
import DepartmentService from '../../services/department.service';
import BookingService from '../../services/booking.service';
import TNHContext from '@contexts/TNHContext';
import BookingSuccess from './BookingSuccess';
import ExpertiseService from '@services/expertise.service';
import moment from 'moment';

export default function BookingForm() {
    const {companyId, selectedDoctor, setSelectedDoctor} = useContext(TNHContext);
    const {locale} = useRouter();
    const [businessData, setBusinessData] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [expertises, setExpertises] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [form, setForm] = useState({
        name: '',
        phone: '',
        hospital: '',
        department: '',
        department_name: '',
        doctor: '',
        doctor_name: '',
        expertises: '',
        expertises_name: '',
        time: '',
        symptoms: '',
    });

    const [showSuccess, setShowSuccess] = useState(false);
    const [bookingCode, setBookingCode] = useState('');
    const [errors, setErrors] = useState({});
    const [isFromDoctorSection, setIsFromDoctorSection] = useState(false); // Đánh dấu thông tin đến từ section bác sĩ

    useEffect(() => {
        const businessService = new BusinessService();
        businessService
            .getBusinessHome({companyId, locale})
            .then(response => {
                setBusinessData(response);
            })
            .catch(error => {
                console.error('Error fetching business data:', error);
            });
    }, [locale]);

    useEffect(() => {
        // if (!form.department) {
        //     setDoctors([]);
        //     return;
        // }
        const userService = new UserService();
        userService
            .getUserOptions({
                companyId,
                businessId: form.hospital,
                locale,
                departmentId: form.department,
                expertiseId: form.expertises,
            })
            .then(response => {
                setDoctors(response);
            })
            .catch(error => {
                console.error('Error fetching doctors:', error);
            });

        if (!selectedDoctor) {
            const expertiseService = new ExpertiseService();
            expertiseService
                .getListHome({locale: locale})
                .then(response => {
                    setExpertises(response);
                    console.log(response);
                })
                .catch(error => {
                    console.error('Error fetching expertise data:', error);
                });
        }
    }, [companyId, locale, form.hospital, form.department, form.expertises]);

    useEffect(() => {
        const departmentService = new DepartmentService();
        departmentService
            .getDepartmentList({locale, companyId})
            .then(response => {
                setDepartments(response);
            })
            .catch(error => {
                console.error('Error fetching departments:', error);
            });
    }, [companyId, locale]);

    // Pre-fill form when selectedDoctor changes
    useEffect(() => {
        if (selectedDoctor) {
            // Nếu có flag clearDoctor từ ServiceSection, clear doctor
            if (selectedDoctor.clearDoctor) {
                console.log('[BookingForm] Nhận signal clear doctor từ ServiceSection');
                setForm(prev => ({
                    ...prev,
                    doctor: '', // Clear doctor
                }));
                setIsFromDoctorSection(false); // Reset flag
                setSelectedDoctor(null);
                return;
            }
            const expertiseService = new ExpertiseService();
            const hasAllInfo = !!(selectedDoctor.business_id && selectedDoctor.department_id && selectedDoctor.user_id);
            if (hasAllInfo) {
                setIsFromDoctorSection(true);
                console.log('[BookingForm] Thông tin đến từ section bác sĩ, sẽ disable field doctor');
                expertiseService
                    .getByUser({user_id: selectedDoctor.user_id, locale})
                    .then(response => {
                        setExpertises(response);
                        console.log(response);
                    })
                    .catch(error => {
                        console.error('Error fetching expertise data:', error);
                    });
            } else {
                expertiseService
                    .getListHome({locale: locale})
                    .then(response => {
                        setExpertises(response);
                        console.log(response);
                    })
                    .catch(error => {
                        console.error('Error fetching expertise data:', error);
                    });
            }
            setForm(prev => ({
                ...prev,
                hospital: selectedDoctor.business_id || prev.hospital,
                department: selectedDoctor.department_id || '',
                doctor: selectedDoctor.user_id || '',
                department_name: selectedDoctor.department_name || '',
                doctor_name: selectedDoctor.full_name || '',
            }));
            // Clear selected doctor after pre-filling
            setSelectedDoctor(null);
        }
    }, [selectedDoctor, setSelectedDoctor]);

    // Check xem có đủ 3 thông tin (hospital, department, doctor) và đến từ section bác sĩ để disable field doctor
    const hasAllDoctorInfo = isFromDoctorSection && !!(form.hospital && form.department && form.doctor);

    const handleChange = e => {
        const {name, value} = e.target;
        const text = e.target.selectedOptions?.[0]?.text || '';
        if (name === 'phone') {
            const digits = value.replace(/\D/g, '').slice(0, 11); // keep only numbers, max 11
            setForm(prev => ({...prev, phone: digits}));
            return;
        }

        // Nếu thay đổi department, clear doctor để tránh chọn bác sĩ không thuộc chuyên khoa mới
        if (name === 'department') {
            console.log('Thay đổi khoa ' + value + text);
            setForm(prev => ({
                ...prev,
                department: value,
                department_name: text,
                doctor: '', // Clear doctor khi đổi chuyên khoa
                expertises: '',
                doctor_name: '',
            }));
            // Reset flag khi user thay đổi department
            setIsFromDoctorSection(false);
        }
        if (name === 'expertises') {
            console.log('Thay đổi chuyên khoa ' + value + text);
            setForm(prev => ({
                ...prev,
                expertises: value,
                expertises_name: text,
            }));
            // Reset flag khi user thay đổi department
            setIsFromDoctorSection(false);
        } else if (name === 'doctor') {
            // Reset flag khi user thay đổi doctor
            setIsFromDoctorSection(false);
            setForm(prev => ({...prev, doctor: value, doctor_name: text}));
        } else {
            setForm(prev => ({...prev, [name]: value}));
        }
    };

    const handlePhoneKeyDown = e => {
        // Allow control keys
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Escape', 'Enter'];
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) {
            return;
        }
        // Block non-numeric keys
        if (!/[0-9]/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleTimeKeyDown = e => {
        // Allow Tab for accessibility
        if (e.key === 'Tab') return;
        // Prevent all other keyboard input
        e.preventDefault();
    };

    const handleTimeFocus = e => {
        // Auto-trigger the picker on focus
        try {
            e.target.showPicker?.();
        } catch (err) {
            console.warn('showPicker not supported:', err);
        }
    };

    const handleTimeClick = e => {
        // Also trigger on click
        try {
            e.target.showPicker?.();
        } catch (err) {
            console.warn('showPicker not supported:', err);
        }
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = getLocaleContent(locale, 'page.booking-form.validation.full-name-required');

        if (!form.phone.trim()) errs.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-required');
        else if (!/^[0-9]{10,11}$/.test(form.phone.trim()))
            errs.phone = getLocaleContent(locale, 'page.booking-form.validation.phone-matches');

        if (!form.hospital) errs.hospital = getLocaleContent(locale, 'page.booking-form.validation.hospital-required');

        if (!form.time) errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-required');
        else {
            const m = moment(form.time);
            if (!m.isValid()) errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-matches');
            else if (m.isBefore(moment()))
                errs.time = getLocaleContent(locale, 'page.booking-form.validation.time-past');
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!validate()) return;

        // get business_code from businessData
        const selectedBusiness = businessData.find(b => b.business_id === form.hospital);
        const business_code = selectedBusiness ? selectedBusiness.business_code : '';

        const bookingData = {
            ...form,
            business_code: business_code,
            time: `${form.time}:00`, // Append seconds to the time
        };

        const bookingService = new BookingService();
        bookingService
            .makeABooking(bookingData)
            .then(response => {
                if (response.code === 0) {
                    setBookingCode(response?.Extent?.MaDK || '');
                    setShowSuccess(true);
                    setForm({
                        name: '',
                        phone: '',
                        hospital: '',
                        department: '',
                        doctor: '',
                        expertises: '',
                        expertises_name: '',
                        time: '',
                        symptoms: '',
                    });
                } else {
                    alert(getLocaleContent(locale, 'page.booking-form.error-message'));
                }
                // Reset flag khi submit thành công
                setIsFromDoctorSection(false);
            })
            .catch(error => {
                alert(getLocaleContent(locale, 'page.booking-form.error-message'));
            });
    };

    return (
        <>
            <div id="dv-dat-lich" className={styles['container']}>
                <div className={`${styles['booking-container']} container`}>
                    {/* Left Text Section */}
                    <div className={styles['booking-info']}>
                        <h2 className={styles['title-red']}>{getLocaleContent(locale, 'common.booking')}</h2>
                        <h2 className={styles['title-green']}>
                            {getLocaleContent(locale, 'page.booking-form.booking-easy')}
                        </h2>
                        <p>{getLocaleContent(locale, 'page.booking-form.description')}</p>
                        <Link href="/lien-he-tnh">
                            <button className={styles['learn-more']}>
                                <img src="/bw_image/Info.svg" alt="Info" className={styles['info-icon']} />
                                {getLocaleContent(locale, 'common.discover-more')}
                            </button>
                        </Link>
                    </div>

                    {/* Form Section */}
                    <div className={styles['booking-form']}>
                        <h3 className={styles['form-title']}>{getLocaleContent(locale, 'page.booking-form.title')}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className={styles['form-grid']}>
                                <div className={styles['form-group']}>
                                    <label htmlFor="name">
                                        {getLocaleContent(locale, 'page.booking-form.full-name')}
                                        <span style={{color: 'red'}}> (*)</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder={getLocaleContent(
                                            locale,
                                            'page.booking-form.full-name-placeholder',
                                        )}
                                    />
                                    {errors.name && <div className={styles['error-message']}>{errors.name}</div>}
                                </div>

                                <div className={styles['form-group']}>
                                    <label htmlFor="phone">
                                        {getLocaleContent(locale, 'page.booking-form.phone')}
                                        <span style={{color: 'red'}}> (*)</span>
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        onKeyDown={handlePhoneKeyDown}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={11}
                                        placeholder={getLocaleContent(locale, 'page.booking-form.phone-placeholder')}
                                    />
                                    {errors.phone && <div className={styles['error-message']}>{errors.phone}</div>}
                                </div>

                                <div className={styles['form-group']}>
                                    <label htmlFor="hospital">
                                        {getLocaleContent(locale, 'page.booking-form.hospital')}
                                        <span style={{color: 'red'}}> (*)</span>
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            id="hospital"
                                            name="hospital"
                                            value={form.hospital}
                                            onChange={handleChange}>
                                            <option value="">
                                                {getLocaleContent(locale, 'page.booking-form.choose-hospital')}
                                            </option>
                                            {businessData.map(business => (
                                                <option key={business.business_id} value={business.business_id}>
                                                    {business.business_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.hospital && (
                                        <div className={styles['error-message']}>{errors.hospital}</div>
                                    )}
                                </div>

                                {/* <div className={styles['form-group']}>
                                    <label htmlFor="department">
                                        {getLocaleContent(locale, 'page.booking-form.department')}
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            id="department"
                                            name="department"
                                            value={form.department}
                                            onChange={handleChange}>
                                            <option value="">
                                                {getLocaleContent(locale, 'page.booking-form.choose-department')}
                                            </option>
                                            {departments.map(department => (
                                                <option key={department.department_id} value={department.department_id}>
                                                    {department.department_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div> */}

                                <div className={styles['form-group']}>
                                    <label htmlFor="expertise">
                                        {getLocaleContent(locale, 'page.booking-form.expertise')}
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            id="expertises"
                                            name="expertises"
                                            value={form.expertises}
                                            onChange={handleChange}>
                                            <option value="">
                                                {getLocaleContent(locale, 'page.booking-form.choose-expertise')}
                                            </option>
                                            {expertises.map(expertise => (
                                                <option key={expertise.expertise_id} value={expertise.expertise_id}>
                                                    {expertise.expertise_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className={styles['form-group']}>
                                    <label htmlFor="doctor">
                                        {getLocaleContent(locale, 'page.booking-form.doctor')}
                                    </label>
                                    <div className="select-wrapper">
                                        <select
                                            id="doctor"
                                            name="doctor"
                                            value={form.doctor}
                                            onChange={handleChange}
                                            disabled={hasAllDoctorInfo}
                                            style={hasAllDoctorInfo ? {opacity: 0.6, cursor: 'not-allowed'} : {}}>
                                            <option value="">
                                                {getLocaleContent(locale, 'page.booking-form.choose-doctor')}
                                            </option>
                                            {doctors.map(doctor => (
                                                <option key={doctor.user_id} value={doctor.user_id}>
                                                    {doctor.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className={styles['form-group']}>
                                    <label htmlFor="time">
                                        {getLocaleContent(locale, 'page.booking-form.time')}
                                        <span style={{color: 'red'}}> (*)</span>
                                    </label>
                                    <div className={styles['datetime-wrapper']}>
                                        <input
                                            type="datetime-local"
                                            id="time"
                                            name="time"
                                            value={form.time}
                                            onChange={handleChange}
                                            onKeyDown={handleTimeKeyDown}
                                            onFocus={handleTimeFocus}
                                            onClick={handleTimeClick}
                                            placeholder={getLocaleContent(locale, 'page.booking-form.time-placeholder')}
                                            style={{cursor: 'pointer'}}
                                        />
                                    </div>
                                    {errors.time && <div className={styles['error-message']}>{errors.time}</div>}
                                </div>
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label htmlFor="symptoms">
                                    {getLocaleContent(locale, 'page.booking-form.symptoms')}
                                </label>
                                <textarea
                                    id="symptoms"
                                    name="symptoms"
                                    value={form.symptoms}
                                    onChange={handleChange}
                                    placeholder={getLocaleContent(locale, 'page.booking-form.symptoms-placeholder')}
                                />
                                {errors.symptoms && <div className={styles['error-message']}>{errors.symptoms}</div>}
                            </div>

                            <div className={styles['submit-btn']}>
                                <button type="submit">
                                    {getLocaleContent(locale, 'page.booking-form.send')}
                                    <img
                                        src="/bw_image/arrow-long-right-white.svg"
                                        alt="Arrow Right"
                                        className={styles['arrow-icon']}
                                    />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <BookingSuccess
                open={showSuccess}
                bookingCode={bookingCode}
                onClose={() => {
                    setShowSuccess(false);
                    setBookingCode('');
                }}
            />
        </>
    );
}
